import { useQuery, useMutation, useQueryClient } from 'react-query'
import { pessoaService } from '../services/pessoa'
import { PessoaFilters, PessoaFormData, CriarParametrizacaoPayload, CargoDisponivel, TipoContratacao, TipoPremiacao, PessoaParametrizacao, TipoParametroPessoa } from '../tipos/pessoa'
import { useToast } from '../../../contexts/NotificationContext'

export function usePessoas(filters?: PessoaFilters) {
  const toast = useToast()

  // usePessoas é usado apenas nas rotinas de manutenção (Pessoas e Pessoas e Times),
  // que devem exibir também as pessoas vinculadas a times externos.
  return useQuery(['pessoas', filters], () => pessoaService.listarPessoas(filters, true), {
    keepPreviousData: true,
    onError: (error: any) => {
      const mensagem = error?.message || 'Erro ao carregar pessoas'
      toast.error(mensagem)
    },
  })
}

export function usePessoa(id?: number) {
  const toast = useToast()

  return useQuery(['pessoa', id], () => pessoaService.getPessoaById(id!), {
    enabled: !!id,
    onError: (error: any) => {
      const mensagem = error?.message || 'Erro ao carregar pessoa'
      toast.error(mensagem)
    },
  })
}

export function useCreatePessoa() {
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation(pessoaService.criarPessoa, {
    onSuccess: () => {
      toast.success('Pessoa criada com sucesso!')
      queryClient.invalidateQueries(['pessoas'])
      queryClient.invalidateQueries(['usuarios-pessoas-ativas']) // Invalidar também a lista de pessoas ativas usada no formulário de usuário
    },
      onError: (error: any) => {
        const detalhes = error?.response?.data
        
        // Se detalhes é uma string, usar diretamente
        if (typeof detalhes === 'string') {
          toast.error(detalhes)
          return
        }
        
        const mensagens =
          detalhes?.errors?.messages ??
          detalhes?.errors ??
          (detalhes && typeof detalhes === 'object' ? Object.values(detalhes).flat() : [])

        const mensagem = Array.isArray(mensagens) && mensagens.length > 0
          ? mensagens.join('\n')
          : error?.message || 'Erro ao criar pessoa'

        toast.error(mensagem)
      },
  })
}

export function useUpdatePessoa() {
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation(
    ({ id, data }: { id: number; data: PessoaFormData }) => pessoaService.atualizarPessoa(id, data),
    {
      onSuccess: (_, variables) => {
        toast.success('Pessoa atualizada com sucesso!')
        queryClient.invalidateQueries(['pessoas'])
        queryClient.invalidateQueries(['pessoa', variables.id])
        queryClient.invalidateQueries(['usuarios-pessoas-ativas']) // Invalidar também a lista de pessoas ativas usada no formulário de usuário
      },
      onError: (error: any) => {
        const detalhes = error?.response?.data
        
        // Se detalhes é uma string, usar diretamente
        if (typeof detalhes === 'string') {
          toast.error(detalhes)
          return
        }
        
        const mensagens =
          detalhes?.errors?.messages ??
          detalhes?.errors ??
          (detalhes && typeof detalhes === 'object' ? Object.values(detalhes).flat() : [])

        const mensagem = Array.isArray(mensagens) && mensagens.length > 0
          ? mensagens.join('\n')
          : error?.message || 'Erro ao atualizar pessoa'

        toast.error(mensagem)
      },
    }
  )
}

export function useDeletePessoa() {
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation(pessoaService.excluirPessoa, {
    onSuccess: () => {
      toast.success('Pessoa excluída com sucesso!')
      queryClient.invalidateQueries(['pessoas'])
      queryClient.invalidateQueries(['usuarios-pessoas-ativas']) // Invalidar também a lista de pessoas ativas usada no formulário de usuário
    },
      onError: (error: any) => {
        const detalhes = error?.response?.data
        
        // Se detalhes é uma string, usar diretamente
        if (typeof detalhes === 'string') {
          toast.error(detalhes)
          return
        }
        
        const mensagens =
          detalhes?.errors?.messages ??
          detalhes?.errors ??
          (detalhes && typeof detalhes === 'object' ? Object.values(detalhes).flat() : [])

        const mensagem = Array.isArray(mensagens) && mensagens.length > 0
          ? mensagens.join('\n')
          : error?.message || 'Erro ao excluir pessoa'

        toast.error(mensagem)
      },
  })
}

export function useCargosDisponiveis() {
  const toast = useToast()

  return useQuery<CargoDisponivel[]>(['cargos-disponiveis'], () => pessoaService.listarCargos(), {
    staleTime: 5 * 60 * 1000,
    onError: (error: any) => {
      const mensagem = error?.message || 'Erro ao carregar cargos'
      toast.error(mensagem)
    },
  })
}

export function useTiposContratacao() {
  const toast = useToast()
  return useQuery<TipoContratacao[]>(['tipos-contratacao'], () => pessoaService.listarTiposContratacao(), {
    staleTime: 10 * 60 * 1000,
    onError: (error: any) => {
      toast.error(error?.message || 'Erro ao carregar tipos de contratação')
    },
  })
}

export function useTiposPremiacao() {
  const toast = useToast()
  return useQuery<TipoPremiacao[]>(['tipos-premiacao'], () => pessoaService.listarTiposPremiacao(), {
    staleTime: 10 * 60 * 1000,
    onError: (error: any) => {
      toast.error(error?.message || 'Erro ao carregar tipos de premiação')
    },
  })
}

export function useParametrizacoes(pessoaId?: number, tipoParametro?: TipoParametroPessoa) {
  const toast = useToast()
  return useQuery<PessoaParametrizacao[]>(
    ['pessoa-parametrizacoes', pessoaId, tipoParametro],
    () => pessoaService.listarParametrizacoes(pessoaId!, tipoParametro),
    {
      enabled: !!pessoaId,
      onError: (error: any) => {
        toast.error(error?.message || 'Erro ao carregar parametrizações')
      },
    }
  )
}

export function useCriarParametrizacao() {
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation(
    ({ pessoaId, data }: { pessoaId: number; data: CriarParametrizacaoPayload }) =>
      pessoaService.criarParametrizacao(pessoaId, data),
    {
      onSuccess: (_, variables) => {
        toast.success('Parametrização criada com sucesso!')
        queryClient.invalidateQueries(['pessoa-parametrizacoes', variables.pessoaId])
        queryClient.invalidateQueries(['pessoas'])
      },
      onError: (error: any) => {
        const detalhes = error?.response?.data
        if (typeof detalhes === 'string') {
          toast.error(detalhes)
          return
        }
        toast.error(error?.message || 'Erro ao criar parametrização')
      },
    }
  )
}
